import { Children, useState, useDeferredValue, useEffect } from "react";
import {
  useDefaultTutorials,
  useTemplateTutorials,
  useAddDefaultTutorial,
  useRemoveDefaultTutorial,
} from "api/templateLibrary/tutorials";
import { AddIcon, TutorialIcon } from "allIcons";
import {
  PopOver,
  Loader,
  TemplateNotFound,
  LoadingListCards,
  BodyContainer,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  AddFromTemplateLibrary,
  FiltersList,
} from "commonUI";
import { useTags } from "api/siteSettings/Tags";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { isEqual, search } from "helpers";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function Tutorials() {
  const { data: defaultTutorials, isLoading: defaultTutorialsLoading } =
    useDefaultTutorials();
  const { data: tutorials } = useTemplateTutorials();
  const [addDefaultTutorialModal, setAddDefaultTutorialModal] = useState(false);
  const addDefaultTutorialMutation = useAddDefaultTutorial();
  const deleteDefaultTutorialMutation = useRemoveDefaultTutorial();

  const { data: tags } = useTags();

  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [filteredDefaultTutorials, setFilteredDefaultTutorials] = useState([]);
  const [filterData, setFilterData] = useState({});
  const { remove_items_from_site_template, edit_templates } =
    useUserPermissions();

  useEffect(() => {
    setFilteredDefaultTutorials(
      defaultTutorials.filter((tutorial) =>
        search(tutorial.title, defferedSearch)
      )
    );
  }, [defaultTutorials, defferedSearch]);

  const applySorting = (order, orderBy) => {
    setFilteredDefaultTutorials((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setFilteredDefaultTutorials(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        defaultTutorials
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        {deleteDefaultTutorialMutation.isLoading ||
        addDefaultTutorialMutation.isLoading ? (
          <Loader loader="block" />
        ) : (
          ""
        )}
        <HeaderContainer>
          <PrimaryHeading>
            {t("libraryTemplate.tutorials.title")}
          </PrimaryHeading>
          <div className="relative">
            <MainButton
              onClick={() =>
                setAddDefaultTutorialModal(!addDefaultTutorialModal)
              }
            >
              <AddIcon />
              {t("button.add")}
            </MainButton>
            <PopOver
              open={addDefaultTutorialModal}
              onClose={setAddDefaultTutorialModal}
            >
              <AddFromTemplateLibrary
                data={
                  tutorials
                    .filter((tutorial) => !isEqual(tutorial.default_tem, 1))
                    .map((tutorial) => ({
                      ...tutorial,
                      icon: <TutorialIcon fill="white" />,
                      onClick: () =>
                        addDefaultTutorialMutation.mutate({ id: tutorial.id }),
                    })) || []
                }
                emptyTemplate={{
                  label: t("libraryTemplate.tutorials.title"),
                  linkLabel: t("libraryTemplate.tutorial"),
                  link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/tutorials/add`,
                }}
              />
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(value) => setSearchText(value)}
            showLibrarySource={false}
            tags={tags}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {Children.toArray(
            defaultTutorialsLoading ? (
              <LoadingListCards />
            ) : filteredDefaultTutorials?.length > 0 ? (
              filteredDefaultTutorials.map((tutorial, index) => {
                return (
                  <ListCard
                    data={tutorial.title}
                    onCross={() => {
                      deleteDefaultTutorialMutation.mutate(tutorial.id);
                    }}
                    editIcon={edit_templates}
                    link={
                      edit_templates
                        ? `/${ModulePrefixes.SITE_TEMPLATE}/library-item/tutorials/${tutorial.id}`
                        : ""
                    }
                    strictLink={true}
                    icon={
                      <ListIconWrapper>
                        <TutorialIcon />
                      </ListIconWrapper>
                    }
                    deleteIcon
                    crossIcon={remove_items_from_site_template}
                    showModalonCross
                    deleteTitle={t(
                      "siteSetting.libraryTemplate.removeTemplate.title"
                    )}
                    deleteButtonText={t(
                      "siteSetting.libraryTemplate.removeTemplate.button"
                    )}
                    deleteMessage={t(
                      "siteSetting.libraryTemplate.removeTemplate.message"
                    )}
                  />
                );
              })
            ) : (
              <TemplateNotFound
                label={t("libraryTemplate.tutorials.title")}
                linkLabel={t("libraryTemplate.tutorial")}
                onClick={() =>
                  setAddDefaultTutorialModal(!addDefaultTutorialModal)
                }
              />
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
